import React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import {
  useNavigateToPlan,
  useNavigateToSearch,
  useNavigateToSearchPlanPeek,
} from "./navigation";
import { useSearchId } from "./search";
import { useSearch } from "contexts/search/SearchContextProvider";
import { usePlanId } from "components/common/boundary/PlanId";
import { Panel } from "components/search/types";
import {
  useAreGridSafeHotkeysEnabled,
  useAreHotkeysEnabled,
  useSetAreHotkeysEnabled,
} from "contexts/hotkeys";
import { GridCellSelectionModel } from "@mui/x-data-grid-premium";
import { useStringQueryParamState } from "./useStringQueryParamState";

export interface Hotkeys {
  previousPeriod: string;
  nextPeriod: string;
  previousHeat: string;
  nextHeat: string;
  openPlan: string;
  closePlan: string;
  expandPlan: string;
  planProductionTab: string;
  planInventoryTab: string;
  planSpreadsheetTab: string;
  transposePlanTable: string;
  openProductionInput: string;
  openInventoryInput: string;
  openAvailabilityInput: string;
  debugPage: string;
  historyPage: string;
  constraintsPage: string;
}

export const hotkeys: Hotkeys = {
  ...{
    previousPeriod: ",",
    nextPeriod: ".",
    previousHeat: "left",
    nextHeat: "right",
    openPlan: "1,2,3,4,5,6,7,8,9,0",
    closePlan: "escape",
    expandPlan: "enter",
    planProductionTab: "p",
    planInventoryTab: "i",
    planSpreadsheetTab: "b",
    transposePlanTable: "t",
    openProductionInput: "a",
    openInventoryInput: "s",
    openAvailabilityInput: "d",
    debugPage: "shift+d",
    historyPage: "shift+h",
    constraintsPage: "shift+c",
  },
  ...(JSON.parse((localStorage.hotkeys ?? "{}") as string) as Hotkeys),
};

export const useSearchPageHotkeys = (
  panel: Panel,
  setPanel: (panel: Panel) => void
) => {
  const {
    results: { results },
  } = useSearch();
  const searchId = useSearchId();
  const navigateToSearchPlanPeek = useNavigateToSearchPlanPeek();
  const navigateToSearch = useNavigateToSearch();

  const enabled = useAreHotkeysEnabled();

  useHotkeys(
    hotkeys.openPlan,
    (e) => {
      const plans =
        results.status === "success" ? (results.data?.plans ?? []) : [];
      const index = Number(e.key);
      if (Number.isInteger(index)) {
        const plan = plans[index === 0 ? 9 : index - 1];
        if (plan && searchId !== null) {
          navigateToSearchPlanPeek(searchId, plan.id);
        }
      }
    },
    { enabled }
  );

  useHotkeys(
    hotkeys.closePlan,
    () => {
      // Close an input panel before navigating away from the plan.
      if (panel !== "None") {
        setPanel("None");
      } else if (searchId !== null) {
        navigateToSearch(searchId);
      }
    },
    { enabled }
  );

  useHotkeys(
    hotkeys.openProductionInput,
    () => {
      setPanel("Production");
    },
    {
      enabled,
    }
  );
  useHotkeys(
    hotkeys.openInventoryInput,
    () => {
      setPanel("Inventory");
    },
    {
      enabled,
    }
  );
  useHotkeys(
    hotkeys.openAvailabilityInput,
    () => {
      setPanel("Availabilities");
    },
    {
      enabled,
    }
  );
};

export const usePlanPeekHotKeys = () => {
  const searchId = useSearchId();
  const planId = usePlanId();
  const navigateToPlan = useNavigateToPlan();
  const enabled = useAreHotkeysEnabled();

  useHotkeys(
    hotkeys.expandPlan,
    () => {
      if (searchId !== null) {
        navigateToPlan(planId, searchId);
      }
    },
    { enabled }
  );
};

export type PlanPageTab = "production" | "inventory" | "spreadsheet";

export const usePlanPageTabHotKeys = (): [
  PlanPageTab,
  React.Dispatch<React.SetStateAction<PlanPageTab>>,
] => {
  const enabled = useAreHotkeysEnabled();
  const [selectedTab, setSelectedTab] = useStringQueryParamState<PlanPageTab>(
    "plan_tab",
    "production"
  );
  useHotkeys(
    hotkeys.planProductionTab,
    () => {
      setSelectedTab("production");
    },
    { enabled }
  );
  useHotkeys(
    hotkeys.planInventoryTab,
    () => {
      setSelectedTab("inventory");
    },
    { enabled }
  );
  useHotkeys(
    hotkeys.planSpreadsheetTab,
    () => {
      setSelectedTab("spreadsheet");
    },
    { enabled }
  );
  return [selectedTab, setSelectedTab];
};

export const useToggleHotkey = (
  hotkey: string
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [isOn, setIsOn] = React.useState(false);
  const enabled = useAreHotkeysEnabled();
  useHotkeys(
    hotkey,
    () => {
      setIsOn((isOn) => !isOn);
    },
    { enabled }
  );
  return [isOn, setIsOn];
};

export const useProductionContextTransposeHotkey = () =>
  useToggleHotkey(hotkeys.transposePlanTable);

export const useAdjacentHeatHotkeys = (
  goToPreviousHeat: (() => void) | undefined,
  goToNextHeat: (() => void) | undefined
) => {
  const enabled = useAreGridSafeHotkeysEnabled();
  useHotkeys(hotkeys.previousHeat, () => goToPreviousHeat?.(), {
    enabled: enabled && goToPreviousHeat !== undefined,
  });
  useHotkeys(hotkeys.nextHeat, () => goToNextHeat?.(), {
    enabled: enabled && goToNextHeat !== undefined,
  });
};

/** Disables global hotkeys while a DataGrid cell is selected or edited. */
export const useDisableHotkeysOnCellSelection = () => {
  const setAreHotkeysEnabled = useSetAreHotkeysEnabled();

  const [hasCellsSelected, setHasCellsSelected] = React.useState(false);
  const [isCellEditing, setIsCellEditing] = React.useState(false);

  React.useEffect(() => {
    const shouldDisableHotkeys = hasCellsSelected || isCellEditing;
    setAreHotkeysEnabled(!shouldDisableHotkeys);
  }, [hasCellsSelected, isCellEditing, setAreHotkeysEnabled]);

  const handleCellSelectionModelChange = React.useCallback(
    (cellSelectionModel: GridCellSelectionModel) => {
      const hasSelection = Object.keys(cellSelectionModel).some((rowId) =>
        Object.values(cellSelectionModel[rowId] ?? {}).some(Boolean)
      );
      setHasCellsSelected(hasSelection);
    },
    []
  );

  const handleCellEditStart = React.useCallback(() => {
    setIsCellEditing(true);
  }, []);

  const handleCellEditStop = React.useCallback(() => {
    setIsCellEditing(false);
  }, []);

  return {
    onCellSelectionModelChange: handleCellSelectionModelChange,
    onCellEditStart: handleCellEditStart,
    onCellEditStop: handleCellEditStop,
  };
};
